import React, { useCallback, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { AI_PASTE_MENU_SECTION } from '@atlaskit/editor-common/toolbar/keys';
import type {
	PasteOptionsToolbarPluginConfiguration,
	PasteMenuRuleFactories,
	PasteOptionsToolbarPasteMenuContext,
} from '@atlaskit/editor-plugin-paste-options-toolbar/paste-options-toolbar-plugin-type';
import { expValEquals } from '@confluence/feature-experiments';
import type { JsonLd } from 'json-ld-types';

import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import type { RegisterComponent } from '@atlaskit/editor-ui-control-model/types';
import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';

import { isFirstPartyLink } from '@confluence/content-smartlinks/entry-points/smartLinkBatchHelpers.ts';
import { createSmartLinkEmbedContent } from '@confluence/create-blank-fabric-page/entry-points/dataFunctions.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	OutboundPageTreeMessage,
	useOutboundPageTreeUpdatePublisher,
} from '@confluence/page-tree-refresh-state-container/entry-points/pageTreeOutboundBus.ts';
import {
	withPageTreeStateUpdater,
	type WithPageTreeStateUpdaterProps,
} from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater.ts';

const ADD_LINK_TO_CONTENT_TREE_KEY = 'confluence-paste-menu-add-link-to-content-tree';

const getHostnameFromUrl = (url: string): string => {
	try {
		return new URL(url).hostname || url;
	} catch {
		return url;
	}
};

const i18n = defineMessages({
	addLinkToContentTree: {
		id: 'editor-presets.fullPage.pasteOptionsToolbar.addLinkToContentTree',
		defaultMessage: 'Add link to content tree',
		description:
			'Label for the paste actions menu item that adds a pasted smart link to the content tree.',
	},
	addLinkSuccessTitle: {
		id: 'editor-presets.fullPage.pasteOptionsToolbar.addLinkSuccessTitle',
		defaultMessage: '"{smartlinkName}" added',
		description:
			'Title for the success flag shown after a pasted smart link is added to the content tree.',
	},
	addLinkSuccessDescription: {
		id: 'editor-presets.fullPage.pasteOptionsToolbar.addLinkSuccessDescription',
		defaultMessage:
			'This smart link is now available in the content tree. Move it or change permissions here.',
		description:
			'Description for the success flag shown after a pasted smart link is added to the content tree.',
	},
	goToSmartLink: {
		id: 'editor-presets.fullPage.pasteOptionsToolbar.goToSmartLink',
		defaultMessage: 'Go to smart link',
		description: 'Button label for opening a newly created smart link page.',
	},
	addLinkFailureTitle: {
		id: 'editor-presets.fullPage.pasteOptionsToolbar.addLinkFailureTitle',
		defaultMessage: 'Smart link could not be added to the content tree',
		description:
			'Title for the failure flag shown when a pasted smart link cannot be added to the content tree.',
	},
	addLinkFailureDescription: {
		id: 'editor-presets.fullPage.pasteOptionsToolbar.addLinkFailureDescription',
		defaultMessage: 'Try again.',
		description:
			'Description for the failure flag shown when a pasted smart link cannot be added to the content tree.',
	},
});

const AddLinkToContentTreeItemWithFlags = ({
	createAnalyticsEvent,
	flags,
	parentId,
	pageTreeStateUpdater,
	pasteMenuContext,
	spaceKey,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	parentId?: string;
	pasteMenuContext: PasteOptionsToolbarPasteMenuContext;
	spaceKey?: string;
} & WithFlagsProps &
	WithPageTreeStateUpdaterProps) => {
	const intl = useIntl();
	const publishContentUpdated = useOutboundPageTreeUpdatePublisher(
		OutboundPageTreeMessage.ContentUpdated,
	);
	const isCreatingSmartLinkEmbedRef = useRef(false);
	const currentPastedUrl = pasteMenuContext.getCurrentPastedUrl();

	const handleClick = useCallback(async () => {
		if (isCreatingSmartLinkEmbedRef.current || !currentPastedUrl) {
			return;
		}
		const flagId = ADD_LINK_TO_CONTENT_TREE_KEY;
		isCreatingSmartLinkEmbedRef.current = true;
		try {
			const response = await createSmartLinkEmbedContent({
				createAnalyticsEvent,
				embedUrl: currentPastedUrl,
				parentId,
				spaceKey,
			});
			const actions = response.url
				? [
						{
							content: intl.formatMessage(i18n.goToSmartLink),
							href: response.url,
							onClick: () => {
								void flags.hideFlag(flagId);
							},
						},
					]
				: undefined;
			if (response.contentId) {
				publishContentUpdated({ contentId: response.contentId, parentId });
				const forceFetchPageId = parentId || response.contentId;
				pageTreeStateUpdater.setForceFetchPageId(forceFetchPageId);
			}
			void flags.showSuccessFlag({
				id: flagId,
				title: intl.formatMessage(i18n.addLinkSuccessTitle, {
					smartlinkName: getHostnameFromUrl(currentPastedUrl),
				}),
				description: intl.formatMessage(i18n.addLinkSuccessDescription),
				isAutoDismiss: true,
				actions,
			});
		} catch {
			void flags.showErrorFlag({
				id: flagId,
				title: intl.formatMessage(i18n.addLinkFailureTitle),
				description: intl.formatMessage(i18n.addLinkFailureDescription),
				isAutoDismiss: true,
			});
		} finally {
			isCreatingSmartLinkEmbedRef.current = false;
		}
	}, [
		createAnalyticsEvent,
		currentPastedUrl,
		flags,
		intl,
		parentId,
		pageTreeStateUpdater,
		publishContentUpdated,
		spaceKey,
	]);

	return (
		<ToolbarDropdownItem
			testId="confluence-paste-menu__add-link-to-content-tree"
			elemBefore={<AssetsSchemaIcon size="medium" label="" />}
			onClick={handleClick}
		>
			<FormattedMessage {...i18n.addLinkToContentTree} />
		</ToolbarDropdownItem>
	);
};

const AddLinkToContentTreeItem = withFlags(
	withPageTreeStateUpdater(AddLinkToContentTreeItemWithFlags),
);

export const createPasteOptionsToolbarPluginOptions = ({
	createAnalyticsEvent,
	parentId,
	smartCardClient,
	spaceKey,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	parentId?: string;
	smartCardClient?: {
		fetchData: (url: string) => Promise<JsonLd.Response>;
	};
	spaceKey?: string;
}): PasteOptionsToolbarPluginConfiguration => ({
	pasteMenuButtonsFactory: (
		rules: PasteMenuRuleFactories,
		pasteMenuContext: PasteOptionsToolbarPasteMenuContext | undefined,
	): RegisterComponent[] => {
		if (!pasteMenuContext) {
			return [];
		}
		return [
			{
				type: 'menu-item',
				key: ADD_LINK_TO_CONTENT_TREE_KEY,
				parents: [
					{
						type: AI_PASTE_MENU_SECTION.type,
						key: AI_PASTE_MENU_SECTION.key,
						// Rank 8 — shown after Ask Rovo (rank 7) at the bottom of the paste actions menu.
						rank: 8,
					},
				],
				isHidden: () => {
					const url = pasteMenuContext.getCurrentPastedUrl();
					if (rules.notSingleLinkRule() || !url) {
						return true;
					}
					// Only show for third-party links; 1P (Atlassian) links already live
					// elsewhere in the product surface and don't belong in the content tree.
					return isFirstPartyLink(url);
				},
				isAsyncHidden: async ({ pastedUrl }) => {
					if (!pastedUrl) {
						return false;
					}
					if (
						!expValEquals('confluence_editor_paste_3p_link_actions_menu', 'isEnabled', true) ||
						!smartCardClient
					) {
						return true;
					}
					try {
						const response = await smartCardClient.fetchData(pastedUrl);
						return response.meta?.access !== 'granted';
					} catch {
						// Network / auth errors should hide the action.
						return true;
					}
				},
				component: () => (
					<AddLinkToContentTreeItem
						createAnalyticsEvent={createAnalyticsEvent}
						parentId={parentId}
						pasteMenuContext={pasteMenuContext}
						spaceKey={spaceKey}
					/>
				),
			},
		];
	},
});
